import { i18n } from '@/i18n';
// eslint-disable-next-line import/no-cycle
import {
  stringLogicalOperators,
  numberLogicalOperators,
  dateStringLogicalOperators,
  dateNullLogicalOperators,
  dateNumberLogicalOperators,
  selectionBasedLogicalOperators,
} from '@/composables/segmentations/logicalOperators';
// eslint-disable-next-line import/no-cycle
import {
  AutomatedScenarioState as scenarioState,
  getAllParentsOfBoxToRoot,
} from '@/composables/AutomatedScenarios';
// eslint-disable-next-line import/no-cycle
import { nestCall, NestApiType, toQueryString } from '@/composables/nestApi';
import { Event } from '@/types/events-types';
// eslint-disable-next-line import/no-cycle
import { predefinedEvents } from '@/composables/events/constants';
// eslint-disable-next-line import/no-cycle
import getOrderPropertiesSegments from '@/composables/segmentations/themes/common-segments/orderPropertiesSegment';
// eslint-disable-next-line import/no-cycle
import getCartPropertiesSegments from '@/composables/segmentations/themes/common-segments/cartPropertiesSegment';
// eslint-disable-next-line import/no-cycle
import { fetchCustomSchemas } from '@/composables/custom-data/customData';
// eslint-disable-next-line import/no-cycle
import { getAttributesSegments, getCustomAttributesSegments as getContactCustomAttributesSegments } from '@/composables/segmentations/themes/contacts';
// eslint-disable-next-line import/no-cycle
import { getProductAttributesSegments, getCustomAttributesSegments as getProductCustomAttributesSegments } from '@/composables/segmentations/themes/products';
// eslint-disable-next-line import/no-cycle
import { eventsTypeEnum } from '@/types';
// eslint-disable-next-line import/no-cycle
import { OperatorType } from '@/types/generated-types/graphql';
// eslint-disable-next-line import/no-cycle
import type { FlowStructureDto } from '@/types/flow-structure';
// eslint-disable-next-line import/no-cycle
import { parseOperatorParameters } from '@/helpers/flowParameters';
// eslint-disable-next-line import/no-cycle
import { fetchOrderStatus } from '@/composables/segmentations/helpers/fetchData';

export interface EventMetadata {
  name: string;
  type: 'text' | 'longtext' | 'number' | 'decimal' | 'bool' | 'date' | 'json';
  description: string;
  required: boolean;
  primary: boolean;
  updateData: boolean;
  label?: string;
  targetSchemaType?: 'system' | 'custom';
  targetSchema?: string;
  targetField?: string;
  // When set, the field is a closed list: options are fetched from this source
  // and rendered as a membership dropdown that stores the option value (e.g. the
  // order status id_status), instead of a free-text input. Keeps `type` honest
  // about the stored value type (a string) while constraining the input.
  optionsSource?: 'orderStatus';
}

/**
 * Async option providers for metadata fields declared with `optionsSource`.
 * Each returns `{ label, value }[]` where `value` is what gets stored in the
 * segmentation config (must match the raw value carried by the event data_chain).
 */
const METADATA_OPTIONS_SOURCES: Record<
  NonNullable<EventMetadata['optionsSource']>,
  () => Promise<Array<{ label: string; value: string }>>
> = {
  orderStatus: fetchOrderStatus,
};

/**
 * Get logical operators based on metadata type
 */
const getOperatorsForMetadataType = (metadataName: string, metadataType: string) => {
  switch (metadataType) {
    case 'number':
    case 'decimal':
      return numberLogicalOperators(metadataName);
    case 'date':
      return [
        { type: 'dateNull', data: dateNullLogicalOperators(metadataName) },
        { type: 'dateString', data: dateStringLogicalOperators(metadataName) },
        { type: 'dateNumber', data: dateNumberLogicalOperators(metadataName) },
      ];
    case 'bool':
      return selectionBasedLogicalOperators(metadataName, [
        { label: 'yes', value: 'true' },
        { label: 'no', value: 'false' },
      ]);
    case 'text':
    case 'longtext':
    case 'json':
    default:
      return stringLogicalOperators(metadataName);
  }
};

/**
 * Find webhook trigger boxes in current operator and its parents, get their event codes
 */
export const findWebhookTriggerInParents = (operatorId: string): string[] => {
  const eventCodes: string[] = [];

  // Check current operator first
  const currentOperator = scenarioState.scenario.data.operators[operatorId];
  if (currentOperator?.custom?.id === 'boxtriggerinternalevent' && currentOperator.custom.event) {
    eventCodes.push(currentOperator.custom.event);
  }

  // Then check all parents up to root
  const parents = getAllParentsOfBoxToRoot(operatorId);
  parents.forEach((parentId) => {
    const parentOperator = scenarioState.scenario.data.operators[parentId];
    if (parentOperator?.custom?.id === 'boxtriggerinternalevent' && parentOperator.custom.event) {
      eventCodes.push(parentOperator.custom.event);
    }
  });

  return eventCodes;
};

/**
 * Get all ancestor step IDs for a given step in a flow
 * Traverses the flow graph from the step back to all root ancestors
 */
const getAllAncestorStepIdsInFlow = (
  flow: FlowStructureDto,
  stepId: string,
  visited: Set<string> = new Set(),
): string[] => {
  if (visited.has(stepId)) return [];
  visited.add(stepId);

  const step = flow.steps.find((s) => s.id === stepId);
  if (!step) return [];

  const ancestors: string[] = [];
  const parents = step.parents || [];
  for (const parent of parents) {
    ancestors.push(parent.id);
    ancestors.push(...getAllAncestorStepIdsInFlow(flow, parent.id, visited));
  }

  return ancestors;
};

/**
 * Find TriggerInternalEvent steps in a flow structure (for new flow system)
 * Returns array of event codes found in current step and its ancestors
 */
export const findWebhookTriggerInFlow = (
  flow: FlowStructureDto,
  stepId: string,
): string[] => {
  const eventCodes: string[] = [];
  const TRIGGER_INTERNAL_EVENT_ID = 'boxtriggerinternalevent';

  // Check current step first
  const currentStep = flow.steps.find((s) => s.id === stepId);
  if (currentStep?.type === TRIGGER_INTERNAL_EVENT_ID) {
    const params = parseOperatorParameters(currentStep.parameters);
    if (params.event) {
      eventCodes.push(params.event as string);
    }
  }

  // Get all ancestor steps and check each one
  const ancestorIds = getAllAncestorStepIdsInFlow(flow, stepId);
  for (const ancestorId of ancestorIds) {
    const ancestorStep = flow.steps.find((s) => s.id === ancestorId);
    if (ancestorStep?.type === TRIGGER_INTERNAL_EVENT_ID) {
      const params = parseOperatorParameters(ancestorStep.parameters);
      if (params.event && !eventCodes.includes(params.event as string)) {
        eventCodes.push(params.event as string);
      }
    }
  }

  return eventCodes;
};

/**
 * Get event metadata - first check predefinedEvents, then fetch from API for custom events
 */
export const getEventMetadata = async (codeName: string): Promise<EventMetadata[]> => {
  // Check if it's a predefined event
  if (predefinedEvents[codeName]) {
    const metaData = predefinedEvents[codeName].properties?.metaData;
    return Array.isArray(metaData) ? metaData as EventMetadata[] : [];
  }

  // Otherwise, fetch from API for custom events
  try {
    const query = toQueryString({
      settings: JSON.stringify({
        limit: 100,
        offset: 0,
        fetchAll: true,
      }),
    });
    const params = {
      limit: 100,
      offset: 0,
      fetchAll: true,
      filters: [
        {
          field: 'type',
          value: eventsTypeEnum.EXTERNAL,
          operator: OperatorType.Equals,
        },
      ],
    };

    const result = await nestCall('get', NestApiType.CLIENT_API, '/events', {}, '', undefined, true, params);
    const events: Event[] = result?.data ?? [];
    const event = events.find((e) => e.code_name === codeName);
    if (event?.properties?.metaData) {
      return event.properties.metaData;
    }
    return [];
  } catch (error) {
    console.error('Error fetching event:', error);
    return [];
  }
};

/**
 * Get available metadata names from parent events (checks predefinedEvents first, then API for custom events)
 */
export const getAvailableMetadataNames = async (operatorId: string): Promise<string[]> => {
  const eventCodes = findWebhookTriggerInParents(operatorId);
  const metadataNames: string[] = [];

  await Promise.all(eventCodes.map(async (codeName) => {
    const metadata = await getEventMetadata(codeName);
    metadata.forEach((meta: EventMetadata) => {
      if (!metadataNames.includes(meta.name)) {
        metadataNames.push(meta.name);
      }
    });
  }));

  return metadataNames;
};

/**
 * Extract metadata names used in a segmentation configuration
 * Structure: [ [AND block: [OR conditions]] ]
 */
export const getUsedMetadataNamesFromConfig = (segmentationConfig: any[]): string[] => {
  const usedMetadataNames: string[] = [];

  if (!Array.isArray(segmentationConfig)) {
    return usedMetadataNames;
  }

  // Iterate through AND blocks
  segmentationConfig.forEach((andBlock) => {
    if (Array.isArray(andBlock)) {
      // Iterate through OR conditions in each AND block
      andBlock.forEach((condition: any) => {
        // Check if this condition uses events_metadata
        if (condition?.groupProperty === 'events_metadata' && condition?.id) {
          if (!usedMetadataNames.includes(condition.id)) {
            usedMetadataNames.push(condition.id);
          }
        }
      });
    }
  });

  return usedMetadataNames;
};

/**
 * Validate that all metadata used in segmentation are still available in parent events
 * Returns array of missing metadata names
 */
export const getMissingMetadataInSegmentation = async (
  operatorId: string,
  segmentationConfig: any[],
): Promise<string[]> => {
  const availableMetadata = await getAvailableMetadataNames(operatorId);
  const usedMetadata = getUsedMetadataNamesFromConfig(segmentationConfig);

  return usedMetadata.filter((name) => !availableMetadata.includes(name));
};

/**
 * Add context path to properties recursively
 * @param properties - Array of property definitions
 * @param contextPath - Current context path (e.g., "metaData.orders")
 * @param parentIsArray - Whether the parent property is an array (multiple: true)
 */
const addContextToProperties = (
  properties: any[],
  contextPath: string,
  parentIsArray = false,
): any[] => properties.map((prop) => {
  const newProp = { ...prop };

  // Mark if parent is an array
  if (parentIsArray) {
    newProp.parentIsArray = true;
  }

  // Use existing context if defined, otherwise build from contextPath + id
  if (!prop.context && prop.id) {
    newProp.context = `${contextPath}.${prop.id}`;
  }

  // Recursively add context to sub_properties
  if (prop.sub_properties && Array.isArray(prop.sub_properties)) {
    // Use the property's context (existing or computed) for children
    const childContextPath = newProp.context || contextPath;
    // Pass down whether this property is an array (multiple: true)
    const isArray = prop.multiple === true;

    newProp.sub_properties = addContextToProperties(
      prop.sub_properties,
      childContextPath,
      isArray,
    );
  }

  return newProp;
});

/**
 * Create a simple field property based on field type
 */
const createSimpleFieldProperty = (field: any, schemaId: number, contextPath?: string) => {
  const fieldId = `custom_${schemaId}_${field.name}`;
  let logicalOperators;

  switch (field.type) {
    case 'number':
      logicalOperators = numberLogicalOperators(fieldId);
      break;
    case 'date':
      logicalOperators = [
        { type: 'dateNull', data: dateNullLogicalOperators(fieldId) },
        { type: 'dateString', data: dateStringLogicalOperators(fieldId) },
        { type: 'dateNumber', data: dateNumberLogicalOperators(fieldId) },
      ];
      break;
    case 'boolean':
      logicalOperators = selectionBasedLogicalOperators(fieldId, [
        { label: 'yes', value: true },
        { label: 'no', value: false },
      ]);
      break;
    case 'text':
    default:
      logicalOperators = stringLogicalOperators(fieldId);
      break;
  }

  const property: any = {
    id: fieldId,
    label: field.label || field.name,
    logical_operators: logicalOperators,
    isCustomAttribute: true,
  };

  if (contextPath) {
    property.context = `${contextPath}.${field.name}`;
  }

  return property;
};

/**
 * Get properties for a system schema with context path
 */
const getSystemSchemaProperties = async (
  schemaName: string,
  contextPath?: string,
): Promise<any[] | null> => {
  let properties: any[] | null = null;

  switch (schemaName) {
    case 'orders':
      properties = await getOrderPropertiesSegments();
      break;
    case 'products': {
      const translation = await i18n;
      const [productAttributes, productCustomAttributes] = await Promise.all([
        getProductAttributesSegments(true),
        getProductCustomAttributesSegments(translation.global.t, true),
      ]);
      // Combine lists from both segments
      properties = [
        ...(productAttributes.list || []),
        ...(productCustomAttributes.list || []),
      ];
      break;
    }
    case 'carts':
      properties = await getCartPropertiesSegments();
      break;
    case 'customers': {
      const translation = await i18n;
      const [contactAttributes, contactCustomAttributes] = await Promise.all([
        getAttributesSegments(translation.global.t, 'small', true),
        getContactCustomAttributesSegments(translation.global.t, 'small', true),
      ]);
      // Combine lists from both segments
      properties = [
        ...(contactAttributes.list || []),
        ...(contactCustomAttributes.list || []),
      ];
      break;
    }
    default:
      return null;
  }

  // Add context path to all properties if provided
  if (properties && contextPath) {
    return addContextToProperties(properties, contextPath);
  }

  return properties;
};

/**
 * Get properties for a custom schema with context path
 */
const getCustomSchemaProperties = async (
  customSchemaId: string,
  contextPath?: string,
  allSchemas?: any[],
): Promise<any[]> => {
  // Fetch all schemas if not provided
  const schemas = allSchemas || await fetchCustomSchemas();

  // Find the target schema by id or by schema_name
  const targetSchema = schemas.find(
    (s) => s.id_custom_object?.toString() === customSchemaId
      || s.schema_name === customSchemaId
      || s.table_name === customSchemaId,
  );

  if (!targetSchema || !targetSchema.fields) {
    return [];
  }

  // Build context path for this schema
  const schemaContextPath = contextPath
    ? `${contextPath}.${targetSchema.table_name}`
    : targetSchema.table_name;

  // Create properties for each field
  const properties = await Promise.all(
    targetSchema.fields.map(async (field: any) => {
      // Check if this field has a relationship
      const relationship = targetSchema.relationships?.find(
        (rel: any) => rel.sourceField === field.name,
      );

      // No relationship - simple field
      if (!relationship) {
        return createSimpleFieldProperty(field, targetSchema.id_custom_object, schemaContextPath);
      }

      // Handle system schema relationship
      if (relationship.targetSchemaType === 'system') {
        const nestedContextPath = `${schemaContextPath}.${relationship.targetSchema}`;
        const systemProperties = await getSystemSchemaProperties(
          relationship.targetSchema,
          nestedContextPath,
        );
        if (systemProperties) {
          return {
            id: `custom_${targetSchema.id_custom_object}_${field.name}`,
            label: `${field.name} (${relationship.targetSchema})`,
            context: nestedContextPath,
            sub_properties: systemProperties,
            isCustomAttribute: true,
          };
        }
      }

      // Handle custom schema relationship (recursive)
      if (relationship.targetSchemaType === 'custom') {
        const relatedSchema = schemas.find(
          (s) => s.id_custom_object?.toString() === relationship.targetSchema,
        );
        if (relatedSchema) {
          const subProperties = await getCustomSchemaProperties(
            relationship.targetSchema,
            schemaContextPath,
            schemas,
          );
          return {
            id: `custom_${targetSchema.id_custom_object}_${field.name}`,
            label: `${field.name} (${relatedSchema.schema_name})`,
            context: `${schemaContextPath}.${relatedSchema.table_name}`,
            sub_properties: subProperties,
            isCustomAttribute: true,
          };
        }
      }

      // Fallback to simple field
      return createSimpleFieldProperty(field, targetSchema.id_custom_object, schemaContextPath);
    }),
  );

  return properties.filter((p) => p !== null);
};

/**
 * Generate segment definition from metadata (async to handle schema relationships)
 */
const generateMetadataSegment = async (meta: EventMetadata): Promise<any> => {
  const baseSegment = {
    id: meta.name,
    label: meta.label || meta.description || meta.name,
    context: `metaData.${meta.name}`,
    isMetadata: true,
  };

  // If metadata has a target schema, add sub_properties
  if (meta.targetSchemaType && meta.targetSchema) {
    let subProperties: any[] | null = null;

    // Build context path for the linked schema: metaData.<schemaName>
    const schemaContextPath = `metaData.${meta.targetSchema}`;

    if (meta.targetSchemaType === 'system') {
      subProperties = await getSystemSchemaProperties(meta.targetSchema, schemaContextPath);
    } else if (meta.targetSchemaType === 'custom') {
      // For custom schemas, pass base metaData context, table_name will be appended
      subProperties = await getCustomSchemaProperties(meta.targetSchema, 'metaData');
    }

    if (subProperties && subProperties.length > 0) {
      return {
        ...baseSegment,
        sub_properties: subProperties,
      };
    }
  }

  // Closed-list metadata (e.g. order_status): fetch the options and render a
  // membership dropdown storing the option value, so the config holds the raw
  // status code the event carries — not a free-text label that never matches.
  if (meta.optionsSource && METADATA_OPTIONS_SOURCES[meta.optionsSource]) {
    const options = await METADATA_OPTIONS_SOURCES[meta.optionsSource]();
    return {
      ...baseSegment,
      logical_operators: selectionBasedLogicalOperators(meta.name, options),
    };
  }

  // Simple metadata without schema relationship
  return {
    ...baseSegment,
    logical_operators: getOperatorsForMetadataType(meta.name, meta.type),
  };
};

/**
 * Get events metadata segments for the current operator context
 *
 * @param currentOperatorId - Operator ID for old scenario system
 * @param flowContext - Optional flow context for new flow system { flow, stepId }
 */
const getEventsMetadataSegments = async (
  currentOperatorId?: string,
  flowContext?: { flow: FlowStructureDto; stepId: string },
): Promise<{ id: string; label: string; list: unknown[] } | null> => {
  let eventCodes: string[] = [];

  if (flowContext?.flow && flowContext?.stepId) {
    eventCodes = findWebhookTriggerInFlow(flowContext.flow, flowContext.stepId);
  } else if (currentOperatorId) {
    eventCodes = findWebhookTriggerInParents(currentOperatorId);
  }

  if (eventCodes.length === 0) {
    return null;
  }

  // Fetch metadata for all events in parallel
  const metadataByEvent = await Promise.all(
    eventCodes.map((codeName) => getEventMetadata(codeName)),
  );

  // Flatten and generate segments for all metadata in parallel
  const allMetadata = metadataByEvent.flat();
  const allSegments = await Promise.all(
    allMetadata.map((meta) => generateMetadataSegment(meta)),
  );

  if (allSegments.length === 0) {
    return null;
  }

  return {
    id: 'events_metadata',
    label: 'segmentations.events_metadata.label',
    list: allSegments,
  };
};

/**
 * Fetch all custom events from API
 */
const fetchAllCustomEvents = async (): Promise<Event[]> => {
  try {
    const params = {
      limit: 100,
      offset: 0,
      fetchAll: true,
      filters: [
        {
          field: 'type',
          value: eventsTypeEnum.EXTERNAL,
          operator: OperatorType.Equals,
        },
      ],
    };

    const result = await nestCall('get', NestApiType.CLIENT_API, '/events', {}, '', undefined, true, params);
    return result?.data ?? [];
  } catch (error) {
    console.error('Error fetching custom events:', error);
    return [];
  }
};

/**
 * Get all custom events metadata segments for template builder conditional display.
 * Returns one group per custom event, each containing its metadata properties.
 */
export const getAllCustomEventsMetadataSegments = async (): Promise<{ id: string; label: string; rawLabel?: boolean; list: unknown[] }[]> => {
  const events = await fetchAllCustomEvents();

  if (events.length === 0) {
    return [];
  }

  const segmentGroups: { id: string; label: string; rawLabel?: boolean; list: unknown[] }[] = [];

  // Process each event in parallel
  await Promise.all(
    events.map(async (event) => {
      const metadata = event.properties?.metaData;
      if (!metadata || !Array.isArray(metadata) || metadata.length === 0) {
        return;
      }

      // Generate segments for all metadata of this event
      const segments = await Promise.all(
        metadata.map((meta: EventMetadata) => generateMetadataSegment(meta)),
      );

      if (segments.length > 0) {
        segmentGroups.push({
          id: `events_metadata_${event.code_name}`,
          label: event.name || event.code_name,
          rawLabel: true,
          list: segments,
        });
      }
    }),
  );

  return segmentGroups;
};

/**
 * Get game metadata segments for conditional display when a game widget is detected.
 * Returns a segment group with game_prize_label and game_prize_image as filterable fields.
 */
export const getGameMetadataSegments = (): { id: string; label: string; rawLabel?: boolean; list: unknown[] } | null => {
  const gameMeta: EventMetadata[] = [
    { name: 'game_prize_label', type: 'text', label: 'Nom du lot gagné', description: '', required: false, primary: false, updateData: false },
    { name: 'game_prize_image', type: 'text', label: 'Image du lot gagné', description: '', required: false, primary: false, updateData: false },
  ];

  const segments = gameMeta.map((meta) => ({
    id: meta.name,
    label: meta.label || meta.name,
    context: `metaData.${meta.name}`,
    logical_operators: getOperatorsForMetadataType(meta.name, meta.type),
  }));

  return {
    id: 'events_metadata_game',
    label: 'Jeu',
    rawLabel: true,
    list: segments,
  };
};

export default getEventsMetadataSegments;
